import { normalizeResourceId } from '../api/tags'
import type { TransportError, TransportRequest } from '../api/transport'
import { PLATFORM_REGISTRY_URL } from '../api/urls'

/**
 * Откуда клиент узнаёт, что ресурс вышел из работы (D-02 аудита AUD-P, G4, U0 С6).
 *
 * Источник — ответы самого сервера, которые и так проходят через transport:
 *
 * - реестр Platform: ресурс в списке или карточке выключен (`enabled: false`),
 *   карточка — 404, ресурс пропал из списка, где был раньше (удалён или снят
 *   допуск роли);
 * - любая ресурсная ручка (`…/v1/resources/{rid}/…`) отказала кодом уровня
 *   РЕСУРСА: `resource-not-found`, `resource-disabled`, `resource-not-granted`
 *   (решение прав Platform, которое служба передаёт дословно).
 *
 * Отказ уровня ПРАВА (`policy-denied`, `capability-disabled`) область не
 * закрывает: ресурс в работе, у человека нет одного действия, и стирать его
 * черновик поставки из-за кнопки «Провести» нельзя. Список `me/resources`
 * отфильтрован по одному праву — пропажа rid из него тоже значит «нет права»,
 * а не «ресурс вышел»; область закроет первый же отказ уровня ресурса.
 */
export const RESOURCE_OUT_OF_WORK_CODES: ReadonlySet<string> = new Set([
  'resource-not-found',
  'resource-disabled',
  'resource-not-granted',
])

/** Что ответ сказал об области ресурсов. */
export interface ResourceScopeObservation {
  /** rid, которые вышли из работы по отказу ресурсной ручки. */
  cleared: readonly string[]
  /**
   * rid, которые реестр показал выключенными или удалёнными. Это состояние, а
   * не событие: область закрывается, только если клиент видел rid рабочим.
   */
  retired: readonly string[]
  /** rid, которые сервер только что подтвердил рабочими. */
  active: readonly string[]
}

const NOTHING: ResourceScopeObservation = { cleared: [], retired: [], active: [] }

export type ObservedOutcome = { ok: true; data: unknown } | { ok: false; error: TransportError }

const RESOURCE_SEGMENT = /\/v1\/resources\/([^/?#]+)(?:[/?#]|$)/

function pathOf(url: string): string {
  const end = url.search(/[?#]/)
  return end < 0 ? url : url.slice(0, end)
}

/** rid из адреса ресурсной ручки (`/api/<служба>/v1/resources/{rid}/…`), нормализованный; иначе `null`. */
export function resourceOfUrl(url: string): string | null {
  const match = RESOURCE_SEGMENT.exec(pathOf(url))
  if (match?.[1] === undefined) return null
  let rid: string
  try {
    rid = decodeURIComponent(match[1])
  } catch {
    return null
  }
  return rid.trim() === '' ? null : normalizeResourceId(rid)
}

function isRegistryCard(url: string, rid: string): boolean {
  return pathOf(url).toLowerCase() === `${PLATFORM_REGISTRY_URL}/${encodeURIComponent(rid)}`
}

interface RegistryEntry {
  id: string
  enabled: boolean
}

function registryEntry(value: unknown): RegistryEntry | null {
  if (typeof value !== 'object' || value === null) return null
  const { id: raw, enabled } = value as { id?: unknown; enabled?: unknown }
  // На проводе rid — строка или `{ value }` (ResourceId); декодер пакета отдаёт строку.
  const id = typeof raw === 'object' && raw !== null ? (raw as { value?: unknown }).value : raw
  if (typeof id !== 'string' || id.trim() === '' || typeof enabled !== 'boolean') return null
  return { id: normalizeResourceId(id), enabled }
}

/**
 * Наблюдатель области на один стор (живёт рядом с его transport): помнит, какие
 * rid список реестра показывал, какие клиент видел рабочими и какие уже
 * объявлены вышедшими. Второй отказ того же rid событие не повторяет — иначе
 * сброс кэша перечитал бы запрос, он снова отказал бы, и клиент ушёл бы в цикл.
 * Снова рабочим rid делает успешный ответ ресурсной ручки или реестр с
 * `enabled: true`. Ресурс, выключенный ещё до того, как клиент его увидел,
 * событием не считается: его черновики закроет первый же отказ его службы.
 */
export class ResourceScopeTracker {
  private listed: ReadonlySet<string> | null = null
  private readonly inWork = new Set<string>()
  /** rid → номер запроса, при ответе на который область закрыта. */
  private readonly cleared = new Map<string, number>()
  private sequence = 0

  /** Номер запроса — взять ДО отправки: ответ, начатый до закрытия области, rid снова рабочим не делает. */
  begin(): number {
    this.sequence += 1
    return this.sequence
  }

  /** rid, о выходе которых надо сообщить сейчас (каждый — один раз до возвращения в работу). */
  observe(request: TransportRequest, outcome: ObservedOutcome, started: number = this.begin()): string[] {
    const seen = this.read(request, outcome)
    const fresh: string[] = []
    const leave = (rid: string) => {
      this.inWork.delete(rid)
      if (this.cleared.has(rid)) return
      this.cleared.set(rid, this.sequence)
      fresh.push(rid)
    }
    for (const rid of seen.retired) if (this.inWork.has(rid)) leave(rid)
    for (const rid of seen.cleared) leave(rid)
    for (const rid of seen.active) {
      // Успех, отправленный до закрытия области (летел, пока ресурс выключали), — старая новость, не возвращение.
      const closedAt = this.cleared.get(rid)
      if (closedAt !== undefined && started <= closedAt) continue
      this.cleared.delete(rid)
      this.inWork.add(rid)
    }
    return fresh
  }

  private read(request: TransportRequest, outcome: ObservedOutcome): ResourceScopeObservation {
    const method = request.method ?? 'GET'
    const path = pathOf(request.url).toLowerCase()

    // Сравнивается только полный список: отфильтрованный (`?kind=`, страница) не говорит о пропаже прочих rid.
    if (path === PLATFORM_REGISTRY_URL && method === 'GET') {
      if (request.url !== pathOf(request.url) || Object.keys(request.params ?? {}).length > 0) return NOTHING
      if (!outcome.ok || !Array.isArray(outcome.data)) return NOTHING
      const entries = outcome.data.map(registryEntry)
      // Ответ не по договору — не повод стирать чужую работу.
      if (entries.some((entry) => entry === null)) return NOTHING
      const present = entries as RegistryEntry[]
      const now = new Set(present.map((entry) => entry.id))
      const retired = present.filter((entry) => !entry.enabled).map((entry) => entry.id)
      // Пропавшие из списка — относительно прежнего ответа ЭТОГО стора (удалён или снят допуск роли).
      if (this.listed !== null) for (const rid of this.listed) if (!now.has(rid)) retired.push(rid)
      this.listed = now
      return { cleared: [], retired, active: present.filter((entry) => entry.enabled).map((entry) => entry.id) }
    }

    const rid = resourceOfUrl(request.url)
    if (rid === null) return NOTHING
    if (outcome.ok) {
      if (isRegistryCard(request.url, rid)) {
        const entry = registryEntry(outcome.data)
        if (entry === null || entry.id !== rid) return NOTHING
        return entry.enabled ? { ...NOTHING, active: [rid] } : { ...NOTHING, retired: [rid] }
      }
      return { ...NOTHING, active: [rid] }
    }
    const { error } = outcome
    if (RESOURCE_OUT_OF_WORK_CODES.has(error.code)) return { ...NOTHING, cleared: [rid] }
    // Карточка реестра 404 без кода — ресурса больше нет.
    if (isRegistryCard(request.url, rid) && error.status === 404 && method === 'GET') return { ...NOTHING, retired: [rid] }
    return NOTHING
  }
}
